import React, { useEffect, useState } from "react";

// region External libraries
import { Settings } from "@mui/icons-material";
import { IconButton, Radio } from "@mui/material";
import { atom } from "@libraries/jotai";
import { useTranslation } from "react-i18next";
// endregion External libraries
// region Helpers
import { IRoleWithUsers } from "src/helpers/utils";
// endregion Helpers
// region Hooks
import { useIdentity, PERMISSIONS } from "@hooks/useIdentity";
// endregion Hooks
// region Languages
import { UserRightsMessages } from "@languages/interfaces";
// endregion Languages
// region Atoms
import Span from "@atoms/Span";
// endregion Atoms
// region Organisms
import { PageTabProps, useTabsAtom } from "@organisms/PageTabs";
// endregion Organisms
// region Pages
import Role from "@pages/Role";
import { selectedTabAtom, userRightsTabsAtom } from "@pages/UsersRights";
// endregion Pages
// region Styles
import * as Styled from "./styles";
// endregion Styles

interface IRoleCardProps {
  currentRole: IRoleWithUsers;
  selectedValue?: string;
  onClickRadioButton?: (value: string) => void;
  showRadioButton?: boolean;
  children?: React.ReactNode;
}

/** Brand palette for the role swatch — roles have no color field, so one is derived. */
const SWATCH_PALETTE = ["#003563", "#2968A8", "#99CC33", "#CC6600", "#FFB733", "#6b8aab"];

/** Picks a stable swatch color for a role from its id. */
const getSwatchColor = (id: string): string => {
  let hash = 0;

  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i)) % SWATCH_PALETTE.length;
  }

  return SWATCH_PALETTE[hash];
};

const RoleCard: React.FC<IRoleCardProps> = (
  { currentRole, selectedValue, onClickRadioButton, showRadioButton, children }
) => {
  // region hooks
  const { t } = useTranslation();
  const { setSelectedTab, addNewTab } = useTabsAtom(userRightsTabsAtom, selectedTabAtom);
  const { validateUserAccess } = useIdentity();
  const [hasGroupAlterPermission, setHasGroupAlterPermission] = useState(false);
  // endregion hooks

  // region constants
  const isUngrouped = currentRole.id === "ungrouped";
  const memberCount = currentRole.users?.length ?? 0;
  // endregion constants

  // region handlers
  const handleChangeRadioButton = (event: any) => {
    onClickRadioButton && onClickRadioButton(event.target.value);
  };

  const handleSettingsIconClick = () => {
    if (!hasGroupAlterPermission) return;

    const newSelectedTabAtom = atom<number>(0);
    const newTab: PageTabProps = {
      id: currentRole.id,
      tab: {
        title: UserRightsMessages.userControl,
        caption: UserRightsMessages.editRoleTabCaption
      },
      tabPanel: <Role selectedStepTabAtom={newSelectedTabAtom} mode="EDIT" currentRoleId={currentRole.id} />
    };
    const indexNewTab = addNewTab(newTab);

    setSelectedTab(indexNewTab);
  };
  // endregion handlers

  // region effects
  useEffect(() => {
    setHasGroupAlterPermission(validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.GROUPS.ALTER));
  }, [validateUserAccess]);
  // endregion effects

  return (
    <Styled.Container $ungrouped={isUngrouped}>
      <Styled.Header>
        <Styled.NameGroup $ungrouped={isUngrouped}>
          {!isUngrouped && <Styled.Swatch $color={getSwatchColor(currentRole.id)} />}
          <Span>{t(currentRole.name)}</Span>
        </Styled.NameGroup>

        <Styled.Meta>
          <Styled.Count>
            {t(isUngrouped ? UserRightsMessages.userCount : UserRightsMessages.memberCount, { count: memberCount })}
          </Styled.Count>

          {showRadioButton ? (
            <Radio
              value={currentRole.id}
              checked={selectedValue === currentRole.id}
              size="small"
              onClick={handleChangeRadioButton}
              inputProps={{ "aria-label": currentRole.id }}
            />
          ) : (
            <IconButton
              className={!hasGroupAlterPermission ? "no-permission" : ""}
              disabled={isUngrouped || !hasGroupAlterPermission}
              onClick={handleSettingsIconClick}
            >
              <Settings />
            </IconButton>
          )}
        </Styled.Meta>
      </Styled.Header>
      <Styled.Users>
        {children}
      </Styled.Users>
    </Styled.Container>
  );
};

RoleCard.defaultProps = {
  selectedValue: undefined,
  onClickRadioButton: undefined,
  showRadioButton: false,
  children: null
};

export default RoleCard;
